import React, { useCallback, useMemo } from 'react';
import type { ExtendedComponentSchema } from 'formiojs';
import { Components } from 'formiojs';
import type {
  EhsFormioReactProps,
  FormioBuilderInfo,
  FormioBuilderSchema
} from '../';
import { EhsFormioReactComponent } from '../';
import type { Component } from '@formio/react';
import { Select } from '../../../form';
import { intl } from '../../../../lib';
import type { LanguageProperty } from '../../../generated/LanguageProperty';
import { dataGridHelperComponent } from '../helpers/dataGrid';
import { useQuestionnaireValidation } from '../../questionnaire/useQuestionnaireValidation';
import { isStringArray } from '../svsselect/FormioSVSUtil';

//START-EHS-REACT-FORMIO-DEFINITION---------------------------------------------
export type EhsFormioSelectConstructor = {
  classNames: string;
  hint?: string;
  selectOptions: Array<SelectOption>;
};
/**
 * @param classNames a string separated by spaces that will be added as classNames
 * @param hint the optional value that will be shown as placeholder
 * @param selectOptions the options the user will be able to select
 */
export type EhsFormioSelectSettings = {
  readonly classNames?: string;
  readonly hint?: string;
  readonly selectOptions: Array<SelectOption>;
  readonly multiple: boolean;
};
export const EhsFormioSelectType = 'ehsSelect';
export class EhsFormioSelectbox extends EhsFormioReactComponent<
  EhsFormioSelectSettings,
  EhsFormioSelectConstructor
> {
  /**
   * defines the default schema of the custom component
   * https://help.form.io/developers/custom-components#component-methods
   */
  static schema(
    extend?: FormioBuilderSchema
  ): ExtendedComponentSchema<FormioBuilderSchema> {
    return Components.components.field.schema({
      type: EhsFormioSelectType,
      label: 'common.unknown',
      key: EhsFormioSelectType,
      classNames: '',
      hint: '',
      selectOptions: [],
      ...extend
    });
  }
  /**
   * defines how the component will look in the Form Editor (Builder)
   * https://help.form.io/developers/custom-components#component-methods
   */
  static get builderInfo(): FormioBuilderInfo {
    return {
      title: 'Ehs-Selectbox',
      group: 'advanced',
      icon: 'plus-square',
      documentation: '/developers/custom-components',
      weight: 40,
      schema: EhsFormioSelectbox.schema()
    };
  }

  /**
   * defines the fields available in the Editor/Builder
   * https://help.form.io/developers/form-builder#overriding-behavior
   */
  static editForm() {
    return {
      components: [
        {
          type: 'textfield',
          key: 'label',
          label: 'label',
          tooltip: 'Please add only valid translation keys'
        },
        {
          type: 'textfield',
          key: 'hint',
          label: 'hint',
          tooltip:
            'The optional language key that will be used as placeholder ',
          weight: 10
        },
        {
          type: 'textfield',
          key: 'classNames',
          label: 'classNames',
          tooltip:
            ' A string separated by spaces that will be added as classNames',
          weight: 20
        },
        dataGridHelperComponent(
          {
            label: 'selectOptions',
            tooltip: 'The options that can be selected (each Row)',
            key: 'selectOptions',
            weight: 30
          },
          [
            {
              type: 'textfield',
              key: 'selectLabel',
              label: 'selectLabel'
            },
            {
              type: 'textfield',
              key: 'selectValue',
              label: 'selectValue'
            }
          ]
        )
      ]
    };
  }

  //Begin Non static definitions-------------------------------------
  private classNames = '';
  private hint = '';
  private selectOptions = [{ selectLabel: '', selectValue: '' }];
  private multiple = false;
  extractFields(component: Component<EhsFormioSelectConstructor>): void {
    this.classNames = component.classNames;
    this.hint = component.hint
      ? intl(component.hint as LanguageProperty, component.hint)
      : '';
    this.selectOptions = component.selectOptions;
    this.shouldSetValue = true;
    this.dataForSetting = this._data[component.key as string];
    this.multiple = component.multiple ?? false;
  }

  attachEhsReact(): [
    (props: EhsFormioReactProps<EhsFormioSelectSettings>) => JSX.Element,
    EhsFormioSelectSettings
  ] {
    return [
      SelectboxCmp,
      {
        classNames: this.classNames,
        hint: this.hint,
        selectOptions: this.selectOptions,
        multiple: this.multiple
      }
    ];
  }
}
//END-EHS-REACT-FORMIO-DEFINITION-----------------------------------------------

type SelectOption = {
  selectLabel: string;
  selectValue: string;
};

function isArrayOfSelectOption(value: any): value is Array<SelectOption> {
  if (!Array.isArray(value)) {
    return false;
  }
  return (value as Array<any>).every(
    val =>
      typeof val === 'object' &&
      val.hasOwnProperty('selectLabel') &&
      val.hasOwnProperty('selectValue')
  );
}

function findSelectedValues(value: string[], translatedItems: SelectOption[]) {
  const defaultSelectValue: SelectOption[] = [];
  for (const val of value) {
    const match = translatedItems.find(option => option.selectValue === val);
    if (match) {
      defaultSelectValue.push(match);
    }
  }
  return defaultSelectValue;
}

/**
 * The Logic for the Formio-Component.
 *
 * @param props formio {@link EhsFormioSelectSettings} properties
 */
function SelectboxCmp(
  props: Readonly<EhsFormioReactProps<EhsFormioSelectSettings>>
): JSX.Element {
  const { settings, onChange } = props;

  const onChangeWrapper = useCallback(
    (val): void => {
      onChange(val ?? '');
    },
    [onChange]
  );
  const translatedItems = useMemo(() => {
    return settings.selectOptions.map(value => {
      return {
        selectValue: value.selectValue,
        selectLabel: intl(
          value.selectLabel as LanguageProperty,
          value.selectLabel
        )
      };
    });
  }, [settings.selectOptions]);

  if (settings.multiple) {
    return (
      <MultipleSelect
        {...props}
        onChangeWrapper={onChangeWrapper}
        translatedItems={translatedItems}
      />
    );
  }
  return (
    <SingleSelect
      {...props}
      onChangeWrapper={onChangeWrapper}
      translatedItems={translatedItems}
    />
  );
}

type SelectCmpProps = Pick<
  EhsFormioReactProps<EhsFormioSelectSettings>,
  | 'settings'
  | 'label'
  | 'defaultValue'
  | 'value'
  | 'keyForComponent'
  | 'readOnly'
  | 'required'
> & {
  translatedItems: SelectOption[];
  onChangeWrapper: (value: any) => void;
};

function SingleSelect({
  settings,
  value,
  defaultValue,
  label,
  keyForComponent,
  required,
  readOnly,
  translatedItems,
  onChangeWrapper
}: Readonly<SelectCmpProps>) {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );
  const wrapperValue = useMemo(() => {
    // For clearing the value
    if (!value) {
      return null;
    }

    // for selectfields value is an object, not a string, therefore do not remove this check
    if (typeof value != 'string') {
      return value;
    }

    if (typeof value === 'string') {
      return (
        translatedItems.find(option => option.selectValue === value) ?? null
      );
    }

    const defaultSelectValue = translatedItems.find(
      option => option.selectValue === defaultValue
    );

    return defaultSelectValue ?? null;
  }, [value, defaultValue, translatedItems]);

  return (
    <Select
      error={errorMessage}
      itemLabelProp="selectLabel"
      itemValueProp="selectValue"
      items={translatedItems}
      label={label}
      onChange={onChangeWrapper}
      placeholder={settings.hint}
      value={wrapperValue}
      variant="single"
      readOnly={readOnly}
      required={required}
      labelOnTop={isQuestionnaire}
    />
  );
}

type MultiSelectCmpProps = SelectCmpProps & {
  value: SelectOption[] | string[] | string;
};

function MultipleSelect({
  settings,
  value,
  defaultValue,
  label,
  keyForComponent,
  required,
  readOnly,
  translatedItems,
  onChangeWrapper
}: Readonly<MultiSelectCmpProps>) {
  const { errorMessage, isQuestionnaire } = useQuestionnaireValidation(
    keyForComponent
  );
  const wrapperMultiSelectValue = useMemo(() => {
    // For clearing the value
    if (!value || value.length === 0) {
      return [];
    }

    // for selectfields value is an Array of strings or Array of SelectOption objects, not a string, therefore do not remove this check
    if (settings.multiple && isArrayOfSelectOption(value)) {
      return value;
    }

    if (settings.multiple && isStringArray(value)) {
      return findSelectedValues(value, translatedItems);
    }

    return findSelectedValues(defaultValue, translatedItems);
  }, [value, translatedItems, settings.multiple, defaultValue]);

  return (
    <Select
      error={errorMessage}
      itemLabelProp="selectLabel"
      itemValueProp="selectValue"
      items={translatedItems}
      label={label}
      onChange={onChangeWrapper}
      placeholder={settings.hint}
      value={wrapperMultiSelectValue}
      variant="multi"
      readOnly={readOnly}
      required={required}
      labelOnTop={isQuestionnaire}
    />
  );
}
